import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SharedNav from '../../components/feature/SharedNav';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dental Insurance Kerrville TX',
  serviceType: 'Dental Insurance',
  description: 'Affordable dental insurance plans for individuals and families in Kerrville, TX. PPO and HMO dental plans covering cleanings, crowns, and braces. Free quotes from West Insurance.',
  areaServed: {
    '@type': 'City',
    name: 'Kerrville, TX',
    containedInPlace: { '@type': 'State', name: 'Texas' },
  },
  provider: {
    '@type': 'InsuranceAgency',
    name: 'West Insurance',
    telephone: '+18302036201',
    url: SITE_URL,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '243 La Reata Rd S',
      addressLocality: 'Kerrville',
      addressRegion: 'TX',
      postalCode: '78028',
      addressCountry: 'US',
    },
  },
  url: `${SITE_URL}/dental-insurance-kerrville-tx`,
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  '@id': `${SITE_URL}/dental-insurance-kerrville-tx#breadcrumb`,
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Dental Insurance Kerrville TX',
      item: `${SITE_URL}/dental-insurance-kerrville-tx`,
    },
  ],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/dental-insurance-kerrville-tx#webpage`,
  url: `${SITE_URL}/dental-insurance-kerrville-tx`,
  name: 'Affordable Dental Insurance Kerrville TX | Call 830-203-6201',
  description: 'Looking for dental insurance in Kerrville? Call 830-203-6201 for instant quotes on PPO and HMO plans. Save on cleanings, crowns, and braces today.',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  breadcrumb: { '@id': `${SITE_URL}/dental-insurance-kerrville-tx#breadcrumb` },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What does dental insurance cover in Kerrville, TX?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Most dental insurance plans in Kerrville cover preventive care (cleanings, exams, X-rays) at 100%, basic procedures (fillings, extractions) at 70–80%, and major procedures (crowns, root canals, bridges) at 50%. Orthodontic coverage for braces is available on select PPO plans. Call West Insurance at 830-203-6201 for a plan tailored to your needs.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does dental insurance cost in Kerrville, TX?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Individual dental insurance plans in Kerrville, TX typically start around $20–$50/month. Family dental plans range from $60–$150/month depending on the carrier, coverage tier, and number of dependents. West Insurance will compare top Texas dental carriers to find you the most affordable dental insurance plan.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the difference between PPO and HMO dental plans in Kerrville?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A PPO dental plan lets you visit any licensed dentist in Kerrville or across Texas, with better reimbursements for in-network providers. An HMO dental plan requires you to use a specific network of dentists and typically has lower premiums. West Insurance at 830-203-6201 will help you choose the best dental plan for your family and budget.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does dental insurance cover braces and orthodontics in Kerrville?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes — many dental insurance PPO plans include orthodontic coverage for children and some offer adult orthodontic benefits as well. Coverage for braces in Kerrville typically pays 50% up to a lifetime maximum. Ask West Insurance which plans include the strongest orthodontic benefits for your child or family.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I get dental insurance with no waiting period in Kerrville?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Some dental insurance plans in Kerrville offer no waiting periods for preventive care, meaning you can use your cleanings and exams right away. For major procedures like crowns, waiting periods of 6–12 months are common on some plans. West Insurance can identify plans with minimal or waived waiting periods — call 830-203-6201 today.',
      },
    },
  ],
};

const plans = [
  {
    icon: 'ri-tooth-line',
    name: 'PPO Dental Plans',
    desc: 'See any licensed dentist in Kerrville or across Texas. No referrals needed. Best balance of flexibility and comprehensive coverage.',
    tag: 'Most Popular',
    features: ['Any dentist, no referral', 'Cleanings at 100%', 'Crowns & bridges covered', 'Orthodontics available'],
  },
  {
    icon: 'ri-community-line',
    name: 'HMO Dental Plans',
    desc: 'Lower monthly premiums using a network of dentists. Ideal for budget-conscious individuals and families in Kerr County.',
    tag: 'Lowest Cost',
    features: ['Low monthly premiums', 'Preventive care covered', 'Network dentists near you', 'No annual maximums'],
  },
  {
    icon: 'ri-briefcase-2-line',
    name: 'Dental + Vision Bundles',
    desc: 'Combine dental and vision coverage under one affordable plan. Perfect for families and self-employed individuals in Kerrville.',
    tag: 'Best Value',
    features: ['Dental + vision combined', 'One monthly bill', 'Glasses & contacts', 'Family-friendly rates'],
  },
  {
    icon: 'ri-parent-line',
    name: 'Family Dental Plans',
    desc: 'Comprehensive family dental coverage including cleanings, fillings, and pediatric orthodontics — all under one deductible.',
    tag: '',
    features: ['Kids braces included', 'Single family deductible', 'Pediatric coverage', 'Maternity dental care'],
  },
];

const coverageItems = [
  { icon: 'ri-shield-check-line', label: 'Routine Cleanings', sub: '100% covered preventive' },
  { icon: 'ri-microscope-line', label: 'X-Rays & Exams', sub: 'Annual diagnostic coverage' },
  { icon: 'ri-capsule-line', label: 'Fillings', sub: '70–80% basic care' },
  { icon: 'ri-trophy-line', label: 'Crowns & Bridges', sub: '50% major procedures' },
  { icon: 'ri-magic-line', label: 'Braces & Orthodontics', sub: 'Lifetime max benefits' },
  { icon: 'ri-first-aid-kit-line', label: 'Root Canals', sub: 'Major procedure coverage' },
];

export default function DentalInsurancePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'Affordable Dental Insurance Kerrville TX | Call 830-203-6201';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Looking for dental insurance in Kerrville? Call 830-203-6201 for instant quotes on PPO and HMO plans. Save on cleanings, crowns, and braces today.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${SITE_URL}/dental-insurance-kerrville-tx`);

    const scripts: HTMLScriptElement[] = [];
    [
      { id: 'schema-dental-service', data: serviceSchema },
      { id: 'schema-dental-faq', data: faqSchema },
      { id: 'schema-dental-breadcrumb', data: breadcrumbSchema },
      { id: 'schema-dental-webpage', data: webPageSchema },
    ].forEach(({ id, data }) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.id = id;
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
      scripts.push(s);
    });

    return () => {
      document.title = 'West Health Insurance Kerrville TX | Private Health, Group & Medicare';
      scripts.forEach((s) => s.parentNode?.removeChild(s));
    };
  }, []);

  const faqs = faqSchema.mainEntity;

  return (
    <div className="min-h-screen bg-white">
      <SharedNav />

      {/* Hero */}
      <section className="relative pt-20 min-h-[620px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/37853987c05fdeef166821d78dbb59fd.jpg"
            alt="Affordable Dental Insurance for Families in Kerrville TX"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/45 to-black/15"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0077B6]/80 backdrop-blur-sm rounded-full mb-6">
              <i className="ri-tooth-line text-white text-sm"></i>
              <span className="text-white text-sm font-medium">Dental Insurance in Kerrville, TX</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Affordable Dental<br />
              <span className="text-4xl md:text-5xl">Insurance in Kerrville</span>
            </h1>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              Protect your smile and your wallet. West Insurance helps Kerrville individuals and families find the best dental insurance plans — PPO, HMO, and family bundles — covering cleanings, crowns, braces, and more.
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <a href="tel:830-203-6201" className="px-8 py-4 bg-[#0077B6] text-white font-semibold rounded-lg hover:bg-[#005F8C] transition-all whitespace-nowrap cursor-pointer">
                Call 830-203-6201 for a Quote
              </a>
              <Link to="/#contact" className="px-8 py-4 bg-white text-[#0077B6] font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer">
                Get Instant Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#0077B6] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-4 gap-6 text-center">
            {[
              { stat: '$20+', label: 'Plans starting per month' },
              { stat: '100%', label: 'Preventive care covered' },
              { stat: 'PPO & HMO', label: 'Plan types available' },
              { stat: '0', label: 'Cost for our quote service' },
            ].map((item) => (
              <div key={item.label}>
                <div className="text-2xl font-bold text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{item.stat}</div>
                <div className="text-white/75 text-xs">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Covered */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            What Dental Insurance Covers in Kerrville
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-14">
            From routine cleanings to major procedures, dental insurance helps Kerrville families save significantly on oral health care.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {coverageItems.map((item) => (
              <div key={item.label} className="flex items-center gap-4 p-6 bg-[#F0F8FF] rounded-2xl">
                <div className="w-12 h-12 flex items-center justify-center bg-[#0077B6] rounded-xl flex-shrink-0">
                  <i className={`${item.icon} text-white text-xl`}></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900 text-sm">{item.label}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{item.sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Split Section */}
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                Why Kerrville Families Need Dental Insurance
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Without dental insurance, a single crown in Kerrville can cost <strong>$1,200–$2,000</strong> out of pocket. A full set of braces can exceed <strong>$6,000</strong>. Dental insurance dramatically reduces these costs — and covers the preventive care that stops problems before they start.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                West Insurance works with Texas&apos;s top dental carriers so Kerrville residents can compare plans side by side. Our agents are paid by the insurers — never by you — so there&apos;s zero cost for our quote service.
              </p>
              <div className="space-y-3">
                {[
                  'PPO plans with any dentist in Kerrville',
                  'HMO plans with low monthly premiums',
                  'No waiting periods on select preventive plans',
                  'Orthodontic coverage for kids and adults',
                  'Bundle dental + vision for maximum savings',
                ].map((point) => (
                  <div key={point} className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center bg-[#0077B6] rounded-full flex-shrink-0">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                    <span className="text-gray-700 text-sm">{point}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden h-96">
              <img
                src="https://readdy.ai/api/search-image?query=friendly%20female%20insurance%20agent%20explaining%20dental%20insurance%20plan%20options%20to%20a%20young%20couple%20at%20a%20bright%20modern%20office%20desk%20documents%20and%20brochures%20on%20table%20professional%20warm%20natural%20lighting%20white%20and%20teal%20color%20palette%20clean%20contemporary%20Kerrville%20Texas&width=700&height=550&seq=dental-agent-001&orientation=landscape"
                alt="West Insurance Agent Explaining Dental Plans in Kerrville TX"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-20 bg-[#F0F8FF]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Dental Insurance Plans Available in Kerrville
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-14">
            We compare dental insurance from all major Texas carriers to find the best PPO and HMO plans for your budget.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => (
              <div key={plan.name} className="bg-white rounded-2xl p-6 relative flex flex-col">
                {plan.tag && (
                  <div className="absolute -top-3 left-6">
                    <span className="px-3 py-1 bg-[#0077B6] text-white text-xs font-bold rounded-full">{plan.tag}</span>
                  </div>
                )}
                <div className="w-12 h-12 flex items-center justify-center bg-[#F0F8FF] rounded-xl mb-4">
                  <i className={`${plan.icon} text-[#0077B6] text-2xl`}></i>
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-3">{plan.name}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-grow">{plan.desc}</p>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-[#0077B6]"></i>
                      {f}
                    </li>
                  ))}
                </ul>
                <a href="tel:830-203-6201" className="block text-center px-4 py-2 bg-[#0077B6] text-white text-sm font-semibold rounded-lg hover:bg-[#005F8C] transition-colors whitespace-nowrap cursor-pointer">
                  Get Quote
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Dental Insurance FAQs
          </h2>
          <p className="text-gray-600 text-center mb-14">Common questions from Kerrville residents about dental insurance plans</p>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div
                key={faq.name}
                className="bg-[#F0F8FF] rounded-xl overflow-hidden"
              >
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-6 text-left cursor-pointer"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                >
                  <span className="font-semibold text-gray-900 text-sm pr-4">{faq.name}</span>
                  <i className={`${openFaq === idx ? 'ri-subtract-line' : 'ri-add-line'} text-[#0077B6] text-xl flex-shrink-0`}></i>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 text-sm leading-relaxed">{faq.acceptedAnswer.text}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0077B6]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Get Instant Dental Insurance Quotes in Kerrville
          </h2>
          <p className="text-white/85 text-lg mb-8 max-w-xl mx-auto">
            Don&apos;t pay out of pocket for cleanings, crowns, or braces. Call West Insurance at <strong className="text-white">830-203-6201</strong> and we&apos;ll compare the best dental plans in Kerrville for free.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a href="tel:830-203-6201" className="px-8 py-4 bg-white text-[#0077B6] font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer">
              Call 830-203-6201 Now
            </a>
            <Link to="/#contact" className="px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">
              Request a Free Quote
            </Link>
          </div>
          <div className="mt-10 flex items-center justify-center gap-8 flex-wrap">
            <Link to="/private-health-insurance-kerrville-tx" className="text-white/80 text-sm hover:text-white transition-colors cursor-pointer">
              &rarr; Private Health Insurance
            </Link>
            <Link to="/group-health-insurance-kerrville-tx" className="text-white/80 text-sm hover:text-white transition-colors cursor-pointer">
              &rarr; Group Health Insurance
            </Link>
            <Link to="/medicare-kerrville-tx" className="text-white/80 text-sm hover:text-white transition-colors cursor-pointer">
              &rarr; Medicare Plans
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
